import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

const WarrantyContractorsPage = () => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAchForm, setShowAchForm] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState<any>(null);
  const [contractors, setContractors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    activeContractors: 0,
    pendingPayouts: 0,
    achPending: 0,
    avgPerformance: 0
  });

  const [formData, setFormData] = useState({
    legalName: '',
    dba: '',
    licenseNumber: '',
    feid: '',
    ein: '',
    contactName: '',
    email: '',
    phone: '',
    businessAddress: '',
    insurancePolicy: '',
    bondAmount: '',
    serviceArea: '',
    certifications: [] as string[]
  });

  const [achData, setAchData] = useState({
    achRouting: '',
    achAccount: '',
    w9FileUrl: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!supabase) return;

    try {
      // Load contractors
      const { data: contractorsData } = await supabase
        .from('contractors')
        .select('*')
        .order('legal_name');

      setContractors(contractorsData || []);

      // Load pending payments for stats
      const { data: paymentsData } = await supabase
        .from('warranty_payments')
        .select('amount_cents')
        .in('status', ['scheduled', 'processing']);

      const pendingPayoutsTotal = paymentsData?.reduce((sum, payment) => sum + payment.amount_cents, 0) || 0;

      // Calculate stats
      const activeContractors = contractorsData?.filter(c => c.active).length || 0;
      const achPending = contractorsData?.filter(c => !c.ach_routing || !c.ach_account).length || 0;
      const avgPerformance = contractorsData && contractorsData.length > 0
        ? contractorsData.reduce((sum, c) => sum + (c.performance_rating || 0), 0) / contractorsData.length
        : 0;

      setStats({
        activeContractors,
        pendingPayouts: Math.round(pendingPayoutsTotal / 100),
        achPending,
        avgPerformance: Math.round(avgPerformance * 10) / 10
      });

    } catch (error) {
      console.error('Error loading contractors:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase) return;

    try {
      // Create contractor
      const { data: contractorData, error: contractorError } = await supabase
        .from('contractors')
        .insert({
          legal_name: formData.legalName,
          dba: formData.dba || null,
          license_number: formData.licenseNumber,
          feid: formData.feid || null,
          ein: formData.ein || null,
          active: true,
          performance_rating: 0,
          total_jobs: 0
        })
        .select()
        .single();

      if (contractorError) throw contractorError;

      // Create contact person
      if (formData.contactName) {
        const { error: personError } = await supabase
          .from('people')
          .insert({
            role: 'contractor_user',
            first_name: formData.contactName.split(' ')[0],
            last_name: formData.contactName.split(' ').slice(1).join(' ') || '',
            email: formData.email,
            phone: formData.phone
          });

        if (personError) console.warn('Contact person creation failed:', personError);
      }

      // Reset form and reload data
      setFormData({
        legalName: '',
        dba: '',
        licenseNumber: '',
        feid: '',
        ein: '',
        contactName: '',
        email: '',
        phone: '',
        businessAddress: '',
        insurancePolicy: '',
        bondAmount: '',
        serviceArea: '',
        certifications: []
      });
      setShowAddForm(false);
      loadData();

    } catch (error) {
      console.error('Error creating contractor:', error);
      alert('Error creating contractor. Please try again.');
    }
  };

  const handleAchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!supabase || !selectedContractor) return;

    try {
      const { error } = await supabase
        .from('contractors')
        .update({
          ach_routing: achData.achRouting,
          ach_account: achData.achAccount,
          w9_file_url: achData.w9FileUrl || null
        })
        .eq('id', selectedContractor.id);

      if (error) throw error;

      // Reset form and reload data
      setAchData({
        achRouting: '',
        achAccount: '',
        w9FileUrl: ''
      });
      setSelectedContractor(null);
      setShowAchForm(false);
      loadData();

    } catch (error) {
      console.error('Error updating ACH info:', error);
      alert('Error updating ACH information. Please try again.');
    }
  };

  const openAchForm = (contractor: any) => {
    setSelectedContractor(contractor);
    setAchData({
      achRouting: contractor.ach_routing || '',
      achAccount: contractor.ach_account || '',
      w9FileUrl: contractor.w9_file_url || ''
    });
    setShowAchForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">👷 Contractor Management</h1>
          <p className="text-slate-400 mt-1">Manage HVAC contractors, ACH setup, and performance tracking</p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg font-semibold"
        >
          ➕ Add Contractor
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-900 p-4 rounded-lg border border-slate-800">
          <div className="text-2xl font-bold text-blue-400">{stats.activeContractors}</div>
          <div className="text-slate-400 text-sm">Active Contractors</div>
        </div>
        <div className="bg-slate-900 p-4 rounded-lg border border-slate-800">
          <div className="text-2xl font-bold text-green-400">${stats.pendingPayouts.toLocaleString()}</div>
          <div className="text-slate-400 text-sm">Pending Payouts</div>
        </div>
        <div className="bg-slate-900 p-4 rounded-lg border border-slate-800">
          <div className="text-2xl font-bold text-yellow-400">{stats.achPending}</div>
          <div className="text-slate-400 text-sm">ACH Setup Pending</div>
        </div>
        <div className="bg-slate-900 p-4 rounded-lg border border-slate-800">
          <div className="text-2xl font-bold text-purple-400">{stats.avgPerformance}/5</div>
          <div className="text-slate-400 text-sm">Avg Performance</div>
        </div>
      </div>

      <div className="bg-slate-900 p-6 rounded-lg border border-slate-800">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input
            type="text"
            placeholder="Search contractors..."
            className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
          />
          <select className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none">
            <option>All Status</option>
            <option>Active</option>
            <option>Pending Setup</option>
            <option>Suspended</option>
          </select>
          <select className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none">
            <option>All Regions</option>
            <option>Central Florida</option>
            <option>North Florida</option>
            <option>South Florida</option>
          </select>
          <select className="p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none">
            <option>All Certifications</option>
            <option>EPA Certified</option>
            <option>NATE Certified</option>
            <option>HVAC Excellence</option>
          </select>
        </div>
      </div>

      <div className="bg-slate-900 rounded-lg border border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-800">
              <tr>
                <th className="p-4 text-left text-slate-300 font-semibold">Company</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Contact</th>
                <th className="p-4 text-left text-slate-300 font-semibold">License</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Service Area</th>
                <th className="p-4 text-left text-slate-300 font-semibold">ACH Status</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Performance</th>
                <th className="p-4 text-left text-slate-300 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400">
                    <div className="animate-spin h-8 w-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4"></div>
                    Loading contractors...
                  </td>
                </tr>
              ) : contractors.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-slate-400">
                    No contractors found. Click "Add Contractor" to add one.
                  </td>
                </tr>
              ) : (
                contractors.map((contractor) => (
                  <ContractorRow
                    key={contractor.id}
                    contractor={contractor}
                    onAchSetup={() => openAchForm(contractor)}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAddForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">👷 Add New Contractor</h2>
              <button
                onClick={() => setShowAddForm(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    value={formData.legalName}
                    onChange={(e) => setFormData({...formData, legalName: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="Enter company name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    DBA (Optional)
                  </label>
                  <input
                    type="text"
                    value={formData.dba}
                    onChange={(e) => setFormData({...formData, dba: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="Doing business as"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="contact@company.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="(XXX) XXX-XXXX"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    HVAC License Number *
                  </label>
                  <input
                    type="text"
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="CAC1817XXX"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Service Area *
                  </label>
                  <select className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none">
                    <option>Select service area</option>
                    <option>Central Florida</option>
                    <option>North Florida</option>
                    <option>South Florida</option>
                    <option>Statewide</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Business Address *
                </label>
                <input
                  type="text"
                  className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                  placeholder="123 Business St, Orlando, FL 32801"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Insurance Policy Number *
                  </label>
                  <input
                    type="text"
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="Insurance policy number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Bond Amount *
                  </label>
                  <input
                    type="number"
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="50000"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Certifications (Optional)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    <span className="text-sm">EPA Certified</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    <span className="text-sm">NATE Certified</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    <span className="text-sm">HVAC Excellence</span>
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    <span className="text-sm">ACCA Member</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg"
                >
                  Add Contractor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ACH Setup Modal */}
      {showAchForm && selectedContractor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-900 p-6 rounded-lg border border-slate-800 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">🏦 ACH Setup - {selectedContractor.legal_name}</h2>
              <button
                onClick={() => setShowAchForm(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleAchSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Routing Number *
                  </label>
                  <input
                    type="text"
                    value={achData.achRouting}
                    onChange={(e) => setAchData({...achData, achRouting: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="9-digit routing number"
                    maxLength={9}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Account Number *
                  </label>
                  <input
                    type="text"
                    value={achData.achAccount}
                    onChange={(e) => setAchData({...achData, achAccount: e.target.value})}
                    className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                    placeholder="Bank account number"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  W-9 Form URL (Optional)
                </label>
                <input
                  type="url"
                  value={achData.w9FileUrl}
                  onChange={(e) => setAchData({...achData, w9FileUrl: e.target.value})}
                  className="w-full p-3 bg-slate-800 rounded border border-slate-700 focus:border-blue-500 focus:outline-none"
                  placeholder="https://example.com/w9-form.pdf"
                />
              </div>

              <div className="bg-slate-800 p-4 rounded-lg">
                <h3 className="font-semibold text-slate-200 mb-2">⚠️ ACH Setup Requirements</h3>
                <ul className="text-sm text-slate-300 space-y-1">
                  <li>• Bank account must be business checking account</li>
                  <li>• Routing number must be valid ABA routing number</li>
                  <li>• Account must be in the contractor's business name</li>
                  <li>• W-9 form required for tax reporting compliance</li>
                </ul>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAchForm(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 rounded-lg"
                >
                  Save ACH Info
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const ContractorRow = ({
  contractor,
  onAchSetup
}: {
  contractor: any;
  onAchSetup: () => void;
}) => {
  const achStatusColors = {
    active: 'bg-green-900 text-green-400',
    pending: 'bg-yellow-900 text-yellow-400',
    suspended: 'bg-red-900 text-red-400'
  };

  const achStatusLabels = {
    active: 'Active',
    pending: 'Setup Pending',
    suspended: 'Suspended'
  };

  // Determine ACH status
  const hasAchSetup = contractor.ach_routing && contractor.ach_account;
  const achStatus = contractor.active ? (hasAchSetup ? 'active' : 'pending') : 'suspended';

  return (
    <tr className="border-b border-slate-800 hover:bg-slate-800/50">
      <td className="p-4">
        <div>
          <div className="font-semibold text-slate-100">{contractor.legal_name}</div>
          <div className="text-sm text-slate-400">License: {contractor.license_number}</div>
          {contractor.dba && <div className="text-sm text-slate-400">DBA: {contractor.dba}</div>}
        </div>
      </td>
      <td className="p-4">
        <div>
          <div className="text-slate-300">Contact Info</div>
          <div className="text-sm text-slate-400">FEID: {contractor.feid || 'N/A'}</div>
          <div className="text-sm text-slate-400">EIN: {contractor.ein || 'N/A'}</div>
        </div>
      </td>
      <td className="p-4">
        <span className="font-mono text-blue-400">{contractor.license_number}</span>
      </td>
      <td className="p-4 text-slate-300">Florida</td>
      <td className="p-4">
        <span className={`px-2 py-1 rounded text-sm font-medium ${achStatusColors[achStatus]}`}>
          {achStatusLabels[achStatus]}
        </span>
      </td>
      <td className="p-4">
        <div>
          <div className="text-slate-300">{contractor.performance_rating || 0}/5 ⭐</div>
          <div className="text-sm text-slate-400">{contractor.total_jobs || 0} jobs</div>
        </div>
      </td>
      <td className="p-4">
        <div className="flex space-x-2">
          <button className="text-blue-400 hover:text-blue-300 text-sm">Edit</button>
          <button
            onClick={onAchSetup}
            className="text-green-400 hover:text-green-300 text-sm"
          >
            ACH Setup
          </button>
          <button className="text-purple-400 hover:text-purple-300 text-sm">View Profile</button>
        </div>
      </td>
    </tr>
  );
};

export default WarrantyContractorsPage;